import { useParams, Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { useState, useEffect, useRef, useMemo } from "react";
import DOMPurify from "dompurify";
import { SEO } from "@/components/SEO";
import { ArticleCard } from "@/components/home/ArticleCard";
import { Comments } from "@/components/Comments";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { OptimizedImage } from "@/components/OptimizedImage";
import { BookmarkButton } from "@/components/BookmarkButton";
import { type Article } from "@/data/articles";
import { slugify, processArticleHtml, type ArticleHeading } from "@/lib/article";
import { useArticle, useArticlesData } from "@/hooks/useArticlesData";
import { useTrackView } from "@/hooks/useTrackView";
import { useArticleAnalytics } from "@/hooks/useArticleAnalytics";
import { useReadingHistory } from "@/hooks/useReadingHistory";
import { ArticlePageSkeleton } from "@/components/skeletons/ArticleCardSkeleton";
import { ArticleAudioPlayer } from "@/components/article/ArticleAudioPlayer";
import { ReaderPreferencesModal, type ReaderPrefs, DEFAULT_READER_PREFS } from "@/components/article/ReaderPreferencesModal";
import { ScrollSpyToc } from "@/components/article/ScrollSpyToc";
import { ImageLightbox } from "@/components/article/ImageLightbox";
import { QuoteSharePill } from "@/components/article/QuoteSharePill";
import { CategoryBadge } from "@/components/ui/CategoryBadge";
import { getCategoryTheme } from "@/lib/categoryTheme";
import { ChevronRight, Link2, Printer, Tag, Home, FileText, Calendar, Clock, User, ZoomIn } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

function ReadingProgressBar({ targetRef, article }: { targetRef: React.RefObject<HTMLElement | null>, article: Article | null | undefined }) {
  const [progress, setProgress] = useState(0);
  const { updateScrollDepth } = useArticleAnalytics(article);

  useEffect(() => {
    const handleScroll = () => {
      if (!targetRef.current) return;
      const el = targetRef.current;
      const rect = el.getBoundingClientRect();
      const total = el.scrollHeight;
      const visible = document.documentElement.clientHeight || window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const pct = Math.min(100, (scrolled / Math.max(1, total - visible)) * 100);
      const nextProgress = Math.max(0, pct);
      setProgress(nextProgress);
      updateScrollDepth(nextProgress);
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [updateScrollDepth, targetRef]);

  return (
    <div className="fixed top-16 left-0 right-0 z-40 h-[3px] bg-transparent pointer-events-none">
      <div
        className="h-full bg-primary transition-[width] duration-100 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const articleRef = useRef<HTMLDivElement>(null);
  const { article, loading, notFound } = useArticle(slug);
  const { articles: allArticles, getByCategory } = useArticlesData();
  useTrackView(article?.id);
  const { trackArticleEvent } = useArticleAnalytics(article);
  const { recordArticleVisit } = useReadingHistory();

  // Reader Preferences state
  const [readerPrefs, setReaderPrefs] = useState<ReaderPrefs>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem("bloxida_reader_prefs");
        return stored ? JSON.parse(stored) : DEFAULT_READER_PREFS;
      } catch {
        return DEFAULT_READER_PREFS;
      }
    }
    return DEFAULT_READER_PREFS;
  });

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const handlePrefsChange = (next: ReaderPrefs) => {
    setReaderPrefs(next);
    try {
      localStorage.setItem("bloxida_reader_prefs", JSON.stringify(next));
    } catch {
      // quota
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Record article visit in reading history (auto-records after 2s of reading)
  useEffect(() => {
    if (article?.id) {
      const cancel = recordArticleVisit({
        id: article.id,
        title: article.title,
        slug: article.slug,
        category: article.categoryLabel,
        image: article.image,
        date: article.date,
      });
      return cancel;
    }
  }, [article, recordArticleVisit]);

  // Compute similar articles: by tag overlap first, then category siblings
  // Shows up to 4 articles for a richer "similar" section
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    const myTags = new Set((article.tags || []).map((t) => t.toLowerCase()));
    if (myTags.size > 0) {
      const tagged = allArticles
        .filter((a) => a.id !== article.id)
        .map((a) => {
          const overlap = (a.tags || []).filter((t) =>
            myTags.has(t.toLowerCase()),
          ).length;
          return { a, overlap };
        })
        .filter((x) => x.overlap > 0)
        .sort((a, b) => b.overlap - a.overlap)
        .map((x) => x.a)
        .slice(0, 4);
      if (tagged.length > 0) return tagged;
    }
    return getByCategory(article.category)
      .filter((a) => a.id !== article.id)
      .slice(0, 4);
  }, [article, allArticles, getByCategory]);

  // Sanitize the article HTML once per content change. Inlining DOMPurify in JSX
  // would re-run it on every render — and the scroll handler calls setProgress on
  // every scroll event, which would re-sanitize the entire document continuously
  // as the reader scrolls. ADD_ATTR preserves TipTap link attributes.
  // Sanitize and process article HTML to guarantee stable heading IDs and scroll margins
  const rawContent = article?.content;
  const { sanitizedContent, headings } = useMemo(() => {
    if (!rawContent) {
      return {
        sanitizedContent: "",
        headings: [
          { id: "introduction", text: "Introduction", level: 2 },
          { id: "final-thoughts", text: "Final Thoughts", level: 2 },
        ] as ArticleHeading[],
      };
    }
    const cleanHtml = DOMPurify.sanitize(rawContent, {
      ADD_ATTR: ["target", "rel", "id", "class"],
    });
    const { processedHtml, headings: extracted } = processArticleHtml(cleanHtml);
    return {
      sanitizedContent: processedHtml,
      headings: extracted.length > 0 ? extracted : [
        { id: "introduction", text: "Introduction", level: 2 },
        { id: "final-thoughts", text: "Final Thoughts", level: 2 },
      ] as ArticleHeading[],
    };
  }, [rawContent]);

  const latestNews = useMemo(() => allArticles.slice(0, 5), [allArticles]);

  // J / K Keyboard Navigation for rapid reading (J = next story, K = previous story)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      ) {
        return;
      }

      if (e.key === "j" || e.key === "J") {
        const next = relatedArticles[0] || latestNews[0];
        if (next) {
          navigate(`/article/${next.slug}`);
          toast.info("Navigated to next story", { duration: 1500 });
        }
      } else if (e.key === "k" || e.key === "K") {
        const prev = relatedArticles[1] || latestNews[1];
        if (prev) {
          navigate(`/article/${prev.slug}`);
          toast.info("Navigated to previous story", { duration: 1500 });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [relatedArticles, latestNews, navigate]);

  if (loading) {
    return <ArticlePageSkeleton />;
  }

  if (notFound || !article) {
    return (
      <div className="container py-20 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">
          Article not found
        </h1>
        <Link to="/" className="text-primary hover:underline">
          Back to Home
        </Link>
      </div>
    );
  }

  const categoryTheme = getCategoryTheme(article.category || article.categoryLabel);
  const ArticleCategoryIcon = categoryTheme.Icon;

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    trackArticleEvent({
      eventName: "article_copy_link",
      method: "copy_link",
      label: article.slug,
    });
    toast.success("Link copied to clipboard", {
      description: "You can now share this article with your network.",
    });
  };

  const shareArticle = (method: "facebook" | "x") => {
    trackArticleEvent({
      eventName: "article_share",
      method,
      label: article.slug,
    });
    const url = encodeURIComponent(window.location.href);
    if (method === "facebook") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        "_blank",
      );
      return;
    }
    window.open(
      `https://twitter.com/intent/tweet?url=${url}&text=${encodeURIComponent(article.title)}`,
      "_blank",
    );
  };

  const printArticle = () => {
    trackArticleEvent({
      eventName: "article_print",
      method: "print",
      label: article.slug,
    });
    window.print();
  };

  const hasRichContent = !!article.content && article.content.trim().length > 0;

  return (
    <>
      <ReadingProgressBar targetRef={articleRef} article={article} />

      <SEO
        title={`${article.title} — Bloxida`}
        description={article.excerpt}
          image={article.image}
          type="article"
          publishedTime={article.publishedAt}
          modifiedTime={article.updatedAt}
          category={article.categoryLabel}
          author={article.authorName}
          keywords={article.tags}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item:
                  typeof window !== "undefined"
                    ? window.location.origin
                    : "https://bloxida.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.categoryLabel,
                item:
                  typeof window !== "undefined"
                    ? `${window.location.origin}/category/${article.category}`
                    : `https://bloxida.com/category/${article.category}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item:
                  typeof window !== "undefined"
                    ? window.location.href
                    : `https://bloxida.com/article/${article.slug}`,
              },
            ],
          }}
        />
        <motion.div 
          className="container pt-5 pb-2"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors flex items-center gap-1">
              <Home className="h-3.5 w-3.5" /> Bloxida
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link
              to={`/category/${article.category}`}
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              <ArticleCategoryIcon className="h-3.5 w-3.5" style={{ color: categoryTheme.color }} /> {article.categoryLabel}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium line-clamp-1 max-w-[200px] flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" /> {article.title}
            </span>
          </nav>
        </motion.div>

        <motion.div 
          className="container pb-12"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            
            {/* LEFT STICKY RAIL: Table of Contents (visible on xl screens) */}
            <aside className="hidden xl:block xl:col-span-3 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2 scrollbar-hide">
              <ScrollSpyToc headings={headings} />
            </aside>

            {/* CENTER PRIMARY COLUMN: Main Article (col-span-1 lg:col-span-8 xl:col-span-6) */}
            <div className="col-span-1 lg:col-span-8 xl:col-span-6 w-full min-w-0" ref={articleRef}>
              <div className="mb-5">
                <CategoryBadge
                  category={article.category || article.categoryLabel || "general"}
                  label={article.categoryLabel}
                  variant="solid"
                  size="sm"
                  showIcon
                  asLink
                  className="mb-3"
                />
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-foreground leading-[1.2] tracking-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1.5 font-medium"><Calendar className="h-3.5 w-3.5" /> {article.date}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1.5 font-medium"><Clock className="h-3.5 w-3.5" /> {article.readingTime} read</span>
                  {article.authorName && (
                    <>
                      <span>·</span>
                      <span className="flex items-center gap-1.5 font-medium"><User className="h-3.5 w-3.5" /> By {article.authorName}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between py-3.5 border-y border-border/60 mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button
                    onClick={() => shareArticle("facebook")}
                    className="h-9 w-9 flex items-center justify-center rounded-xl bg-[hsl(220,46%,48%)] text-white hover:opacity-90 transition-all active:scale-95 shadow-sm"
                    aria-label="Share on Facebook"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => shareArticle("x")}
                    className="h-9 w-9 flex items-center justify-center rounded-xl bg-foreground text-background hover:opacity-90 transition-all active:scale-95 shadow-sm"
                    aria-label="Share on X"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </button>
                  <button
                    onClick={copyLink}
                    className="h-9 w-9 flex items-center justify-center rounded-xl border border-border/70 text-foreground hover:bg-accent transition-all active:scale-95 shadow-sm"
                    aria-label="Copy link"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={printArticle}
                    className="h-9 w-9 flex items-center justify-center rounded-xl border border-border/70 text-foreground hover:bg-accent transition-all active:scale-95 shadow-sm"
                    aria-label="Print"
                  >
                    <Printer className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <BookmarkButton articleId={article.id} variant="labeled" />
                  <ReaderPreferencesModal
                    prefs={readerPrefs}
                    onChange={handlePrefsChange}
                  />
                </div>
              </div>

              {/* Web Speech Audio Narration Player */}
              <ArticleAudioPlayer
                title={article.title}
                content={sanitizedContent || article.excerpt}
                readTimeMinutes={parseInt(article.readingTime) || 4}
                className="mb-8"
              />

              {/* Click-to-zoom Hero Graphic */}
              <div
                onClick={() => setLightboxSrc(article.image)}
                className="relative group cursor-zoom-in overflow-hidden rounded-2xl mb-8 shadow-2xl ring-1 ring-white/10"
                title="Click to view full-resolution image"
              >
                <OptimizedImage
                  src={article.image}
                  alt={article.title}
                  priority
                  width={1200}
                  height={675}
                  aspectClassName="aspect-video"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="h-3.5 w-3.5" /> Click to zoom
                </div>
              </div>

              {/* Contextual Quote Selection Pill */}
              <QuoteSharePill articleTitle={article.title} />

              {/* Fullscreen Image Lightbox Modal */}
              <ImageLightbox
                src={lightboxSrc}
                alt={article.title}
                caption={article.title}
                onClose={() => setLightboxSrc(null)}
              />

              {/* Article Content with Full Width and Reader Typography */}
              <div
                id="article-content"
                className={cn(
                  "w-full min-w-0 leading-relaxed text-foreground",
                  readerPrefs.fontFamily === "serif"
                    ? "font-serif"
                    : readerPrefs.fontFamily === "mono"
                      ? "font-mono"
                      : "font-sans",
                )}
              >
                {hasRichContent ? (
                  <div
                    className="prose prose-base sm:prose-lg max-w-none w-full text-foreground leading-relaxed prose-headings:text-foreground prose-strong:text-foreground prose-a:text-primary prose-blockquote:text-muted-foreground prose-blockquote:border-primary prose-code:text-primary prose-img:rounded-2xl"
                    style={{
                      fontSize:
                        readerPrefs.fontSize === "sm"
                          ? "16px"
                          : readerPrefs.fontSize === "lg"
                            ? "20px"
                            : readerPrefs.fontSize === "xl"
                              ? "23px"
                              : "18px",
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                  />
                ) : (
                  <div
                    className="prose prose-base sm:prose-lg max-w-none w-full text-foreground leading-relaxed prose-headings:text-foreground"
                    style={{
                      fontSize:
                        readerPrefs.fontSize === "sm"
                          ? "16px"
                          : readerPrefs.fontSize === "lg"
                            ? "20px"
                            : readerPrefs.fontSize === "xl"
                              ? "23px"
                              : "18px",
                    }}
                  >
                    <p className="text-muted-foreground leading-relaxed mb-6 text-base sm:text-lg">
                      {article.excerpt}
                    </p>
                    <h2 id="introduction" className="text-xl sm:text-2xl font-bold text-foreground mt-8 mb-4 scroll-mt-24">
                      Introduction
                    </h2>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      In a world where choices are abundant and trends shift
                      quickly, having a reliable guide can make all the
                      difference.
                    </p>
                    <h2 id="final-thoughts" className="text-xl sm:text-2xl font-bold text-foreground mt-8 mb-4 scroll-mt-24">
                      Final Thoughts
                    </h2>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      Making the right choice doesn't have to be overwhelming.
                      With this guide, you have all the information you need to
                      make a confident decision.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT STICKY RAIL: Latest Stories (col-span-1 lg:col-span-4 xl:col-span-3) */}
            <aside className="col-span-1 lg:col-span-4 xl:col-span-3 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto space-y-6 scrollbar-hide">
              <div className="rounded-2xl border border-border/70 bg-card/70 backdrop-blur-md p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-border/50">
                  <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-foreground flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    Latest Stories
                  </h3>
                  <Link
                    to="/recent-articles"
                    className="text-[11px] font-semibold text-primary hover:underline"
                  >
                    View all →
                  </Link>
                </div>

                <div className="space-y-1">
                  {latestNews.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="compact" />
                  ))}
                </div>
              </div>
            </aside>
          </div>

          {/* LOWER ENGAGEMENT BAND: Tags, Subscribe to Bloxida, and Comments (outside sticky reading band) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mt-10">
            {/* Left column spacer on xl to keep center column aligned */}
            <div className="hidden xl:block xl:col-span-3" aria-hidden="true" />

            {/* Center Area: Tags, Newsletter, Comments */}
            <div className="col-span-1 lg:col-span-8 xl:col-span-6 w-full min-w-0 space-y-10">
              {article.tags && article.tags.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap pt-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  {article.tags.map((t) => {
                    const tagSlug = slugify(t);
                    return (
                      <Link
                        key={t}
                        to={`/tag/${tagSlug}`}
                        className="px-3 py-1.5 min-h-[36px] inline-flex items-center rounded-full bg-accent text-foreground text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors active:scale-95 shadow-sm"
                      >
                        #{t}
                      </Link>
                    );
                  })}
                </div>
              )}

              <div>
                <NewsletterSignup source={`article:${article.slug}`} />
              </div>

              <Comments
                articleId={article.id}
                articleTitle={article.title}
                articleSlug={article.slug}
              />
            </div>

            {/* Right column spacer on lg/xl */}
            <div className="hidden lg:block lg:col-span-4 xl:col-span-3" aria-hidden="true" />
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-14 border-t pt-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-primary font-bold text-xl">✦</span>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-foreground">
                      Similar Articles
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {relatedArticles[0]?.tags?.length
                        ? "Selected by shared topics"
                        : `More from ${article.categoryLabel}`}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                >
                  View category →
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} showBookmark />
                ))}
              </div>
            </div>
          )}
        </motion.div>
    </>
  );
}
